import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import Shell from "../components/Shell";
import { buildSagas, findSaga } from "../sagas";

function useSagaRules() {
  const [rules, setRules] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api("/api/sagas/rules")
      .then((data) => {
        if (!cancelled) setRules(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRules([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return rules;
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (!total) return "";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m} min`;
}

function actorNames(video, limit = 4) {
  const list = Array.isArray(video?.actors) ? video.actors : [];
  return list
    .map((a) => (typeof a === "string" ? a : a?.name))
    .filter(Boolean)
    .slice(0, limit);
}

function sagaTopActors(items, limit = 8) {
  const counts = new Map();
  const meta = new Map();
  for (const video of items || []) {
    for (const raw of video.actors || []) {
      const name = typeof raw === "string" ? raw : raw?.name;
      if (!name) continue;
      const key = name.toLowerCase();
      counts.set(key, (counts.get(key) || 0) + 1);
      if (!meta.has(key)) {
        meta.set(key, {
          name,
          profile_url: typeof raw === "object" ? raw.profile_url || null : null,
        });
      } else if (typeof raw === "object" && raw.profile_url && !meta.get(key).profile_url) {
        meta.set(key, { name, profile_url: raw.profile_url });
      }
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "es"))
    .slice(0, limit)
    .map(([key]) => meta.get(key));
}

function SagaPoster({ saga }) {
  const years =
    saga.years?.length > 1
      ? `${Math.min(...saga.years)}–${Math.max(...saga.years)}`
      : saga.years?.[0]
        ? String(saga.years[0])
        : "";

  return (
    <Link to={`/sagas/${saga.slug}`} className="saga-card" draggable={false}>
      <div className="saga-card-media">
        {saga.cover_url ? (
          <img src={saga.cover_url} alt={saga.title} loading="lazy" decoding="async" />
        ) : (
          <div className="poster-fallback">{saga.title.slice(0, 1)}</div>
        )}
        <div className="saga-card-overlay">
          <span className="saga-card-count">{saga.count} películas</span>
          {years ? <span className="saga-card-years">{years}</span> : null}
        </div>
      </div>
      <span className="saga-card-title">{saga.title}</span>
    </Link>
  );
}

export function SagasRow({ videos, customRules }) {
  const sagas = useMemo(
    () => buildSagas(videos, { customRules: customRules || [] }),
    [videos, customRules]
  );
  if (!sagas.length) return null;
  const visible = sagas.slice(0, 9);

  return (
    <section className="row-section row-section-sagas is-inview">
      <div className="row-head">
        <div>
          <p className="row-eyebrow">Franquicias</p>
          <h2>Sagas</h2>
        </div>
        <Link to="/sagas" className="row-more">
          Ver más
        </Link>
      </div>
      <div className="row-slider">
        <div className="poster-row saga-row">
          {visible.map((saga) => (
            <SagaPoster key={saga.slug} saga={saga} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FilmEntry({ video, index }) {
  const rating = video.rating_avg;
  const cast = actorNames(video, 5);
  const duration = formatDuration(video.duration_seconds);
  const synopsis = (video.description || "").trim();

  return (
    <article className="saga-film" style={{ "--film-i": index }}>
      <Link to={`/watch/${video.id}`} className="saga-film-poster">
        {video.cover_url ? (
          <img src={video.cover_url} alt="" loading="lazy" decoding="async" />
        ) : (
          <div className="poster-fallback">{video.title.slice(0, 1)}</div>
        )}
        <span className="saga-film-play" aria-hidden="true">
          <span className="saga-film-play-dot" />
        </span>
      </Link>

      <div className="saga-film-body">
        <div className="saga-film-top">
          <span className="saga-film-order">#{index + 1}</span>
          {video.year ? <time dateTime={String(video.year)}>{video.year}</time> : null}
          {video.category ? <span className="saga-film-cat">{video.category}</span> : null}
          {duration ? <span>{duration}</span> : null}
          {rating ? (
            <span className="saga-film-rating" title={`${rating} / 5`}>
              ★ {rating}
            </span>
          ) : null}
        </div>

        <h3>
          <Link to={`/watch/${video.id}`}>{video.title}</Link>
        </h3>

        {synopsis ? <p className="saga-film-synopsis">{synopsis}</p> : null}

        {cast.length ? (
          <p className="saga-film-cast">
            <strong>Reparto</strong>
            <span>{cast.join(" · ")}</span>
          </p>
        ) : null}

        <Link to={`/watch/${video.id}`} className="saga-film-cta">
          Ver ahora
        </Link>
      </div>
    </article>
  );
}

function useCatalogVideos() {
  const { token, profile } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api("/api/videos", { token })
      .then((data) => {
        if (!cancelled) setVideos(Array.isArray(data) ? data : data?.videos || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "No se pudo cargar el catálogo");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, profile?.id]);

  return { videos, loading, error };
}

export function SagasIndexPage() {
  const { videos, loading, error } = useCatalogVideos();
  const customRules = useSagaRules();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim().toLowerCase();
  const sagas = useMemo(() => {
    const all = buildSagas(videos, { customRules });
    if (!q) return all;
    return all.filter(
      (saga) =>
        saga.title.toLowerCase().includes(q) ||
        saga.items.some((v) => String(v.title || "").toLowerCase().includes(q))
    );
  }, [videos, q, customRules]);

  return (
    <Shell wide>
      <div className="saga-page">
        <section className="saga-hero saga-hero-index">
          <div className="saga-hero-copy">
            <p className="saga-kicker">Colecciones</p>
            <h1>Sagas</h1>
            <p className="saga-hero-lead">
              Franquicias completas ordenadas por año. Elige una saga y sigue la
              historia película a película.
            </p>
          </div>
        </section>

        {loading ? <p className="muted catalog-status">Cargando sagas…</p> : null}
        {error ? <p className="error catalog-status">{error}</p> : null}
        {!loading && !sagas.length ? (
          <p className="muted catalog-status">
            {q
              ? "No hay sagas con esa búsqueda."
              : "Aún no hay sagas (hace falta al menos 2 películas de la misma franquicia)."}
          </p>
        ) : null}

        <div className="saga-grid">
          {sagas.map((saga) => (
            <SagaPoster key={saga.slug} saga={saga} />
          ))}
        </div>
      </div>
    </Shell>
  );
}

function SagaPosterCarousel({ items, onActiveChange }) {
  const covers = useMemo(
    () =>
      (items || []).filter((v) => v.cover_url).map((v) => ({
        id: v.id,
        title: v.title,
        year: v.year,
        cover_url: v.cover_url,
        backdrop_url: v.backdrop_url || v.cover_url,
      })),
    [items]
  );
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const coverKey = covers.map((c) => c.id).join(",");

  useEffect(() => {
    setIndex(0);
  }, [coverKey]);

  useEffect(() => {
    if (covers.length < 2 || paused) return undefined;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % covers.length);
    }, 3800);
    return () => window.clearInterval(timer);
  }, [covers.length, paused]);

  useEffect(() => {
    const current = covers[index];
    if (current && onActiveChange) onActiveChange(current);
  }, [index, coverKey, onActiveChange]);

  if (!covers.length) return null;
  const current = covers[index] || covers[0];

  return (
    <div
      className="saga-poster-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carrusel"
      aria-label="Portadas de la saga"
    >
      <div className="saga-poster-stage">
        {covers.map((item, i) => (
          <Link
            key={item.id}
            to={`/watch/${item.id}`}
            className={`saga-poster-slide ${i === index ? "is-active" : ""}`}
            aria-hidden={i !== index}
            tabIndex={i === index ? 0 : -1}
          >
            <img src={item.cover_url} alt={item.title} />
          </Link>
        ))}
      </div>
      <div className="saga-poster-caption">
        <strong>{current.title}</strong>
        {current.year ? <span>{current.year}</span> : null}
      </div>
      {covers.length > 1 ? (
        <div className="saga-poster-dots" role="tablist" aria-label="Elegir portada">
          {covers.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={i === index ? "is-active" : ""}
              aria-label={item.title}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      ) : null}
      {covers.length > 1 ? (
        <>
          <button
            type="button"
            className="saga-poster-nav saga-poster-prev"
            aria-label="Anterior"
            onClick={() => setIndex((i) => (i - 1 + covers.length) % covers.length)}
          >
            ‹
          </button>
          <button
            type="button"
            className="saga-poster-nav saga-poster-next"
            aria-label="Siguiente"
            onClick={() => setIndex((i) => (i + 1) % covers.length)}
          >
            ›
          </button>
        </>
      ) : null}
    </div>
  );
}

export function SagaDetailPage() {
  const { slug } = useParams();
  const { videos, loading, error } = useCatalogVideos();
  const customRules = useSagaRules();
  const saga = useMemo(
    () => findSaga(videos, slug, { customRules }),
    [videos, slug, customRules]
  );
  const topActors = useMemo(
    () => (saga ? sagaTopActors(saga.items) : []),
    [saga]
  );
  const [heroBg, setHeroBg] = useState("");

  useEffect(() => {
    if (!saga) {
      setHeroBg("");
      return;
    }
    setHeroBg(
      saga.items.find((v) => v.backdrop_url)?.backdrop_url ||
        saga.backdrop_url ||
        saga.cover_url ||
        ""
    );
  }, [saga]);

  const onCarouselActive = useMemo(
    () => (item) => {
      if (item?.backdrop_url || item?.cover_url) {
        setHeroBg(item.backdrop_url || item.cover_url);
      }
    },
    []
  );

  if (!loading && !error && videos.length && !saga) {
    return <Navigate to="/" replace />;
  }

  const yearLabel =
    saga?.years?.length > 1
      ? `${Math.min(...saga.years)} – ${Math.max(...saga.years)}`
      : saga?.years?.[0]
        ? String(saga.years[0])
        : "";

  const totalMinutes = Math.round(
    (saga?.items || []).reduce((sum, v) => sum + (Number(v.duration_seconds) || 0), 0) /
      60
  );

  return (
    <Shell wide>
      <div className="saga-page">
        <section
          className="saga-hero saga-hero-detail"
          style={heroBg ? { "--saga-bg": `url(${heroBg})` } : undefined}
        >
          <div className="saga-hero-bg" aria-hidden="true" />
          <div className="saga-hero-fade" aria-hidden="true" />
          <div className="saga-hero-inner">
            <div className="saga-hero-copy">
              <Link to="/" className="saga-back">
                ← Inicio
              </Link>
              <p className="saga-kicker">Saga</p>
              <h1>{saga?.title || (loading ? "…" : "Saga")}</h1>
              <p className="saga-hero-lead">
                {saga
                  ? `${saga.count} película${saga.count === 1 ? "" : "s"}${
                      yearLabel ? ` · ${yearLabel}` : ""
                    }${totalMinutes ? ` · ~${totalMinutes} min en total` : ""}`
                  : null}
              </p>

              {saga ? (
                <div className="saga-hero-stats">
                  <div>
                    <span>Títulos</span>
                    <strong>{saga.count}</strong>
                  </div>
                  <div>
                    <span>Periodo</span>
                    <strong>{yearLabel || "—"}</strong>
                  </div>
                  <div>
                    <span>Primera</span>
                    <strong>{saga.items[0]?.year || "—"}</strong>
                  </div>
                  <div>
                    <span>Última</span>
                    <strong>{saga.items[saga.items.length - 1]?.year || "—"}</strong>
                  </div>
                </div>
              ) : null}

              {topActors.length ? (
                <div className="saga-hero-cast">
                  <p className="saga-kicker">Reparto habitual</p>
                  <div className="saga-cast-chips">
                    {topActors.map((actor) => (
                      <span key={actor.name} className="saga-cast-chip">
                        {actor.profile_url ? (
                          <img src={actor.profile_url} alt="" loading="lazy" />
                        ) : (
                          <span className="saga-cast-fallback">
                            {actor.name.slice(0, 1)}
                          </span>
                        )}
                        {actor.name}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {saga ? (
              <SagaPosterCarousel items={saga.items} onActiveChange={onCarouselActive} />
            ) : null}
          </div>
        </section>

        {loading ? <p className="muted catalog-status">Cargando…</p> : null}
        {error ? <p className="error catalog-status">{error}</p> : null}

        {saga ? (
          <section className="saga-film-list" aria-label={`Películas de ${saga.title}`}>
            <header className="saga-film-list-head">
              <h2>Películas en orden</h2>
              <p className="muted">Cronológico por año de estreno</p>
            </header>
            {saga.items.map((video, index) => (
              <FilmEntry key={video.id} video={video} index={index} />
            ))}
          </section>
        ) : null}
      </div>
    </Shell>
  );
}
